/**
 * Artifact Studio hooks — thin TanStack Query wrappers over the
 * ArtifactStudioService Connect client (tesoro, via sabaody). These back the
 * Phase 2 UI: the "new artifact" picker (2a-4), the AI-drafted badge +
 * approve/export actions (2c-2), and the editor surface (2b-3).
 */ import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { artifactStudioClient } from '../lib/connect/clients';
const assetsKey = (projectId)=>[
        'artifact-studio',
        'assets',
        projectId
    ];
/**
 * Artifact-type key for the protest decision packet. Mirrors the Go constant
 * officegen.ArtifactTypeProtestDecisionPacket — born ai_drafted and
 * intrinsically legal_review_required. Kept as one named const so callers
 * (e.g. the protest workspace's "Generate decision packet" action) don't
 * duplicate the raw string.
 */ export const PROTEST_DECISION_PACKET_TYPE = 'protest_decision_packet';
/** The artifact types the generator can produce (picker options). */ export function useArtifactTypes() {
    return useQuery({
        queryKey: [
            'artifact-studio',
            'types'
        ],
        queryFn: ()=>artifactStudioClient.listArtifactTypes({}),
        staleTime: 5 * 60 * 1000
    });
}
/** A project's Artifact Studio assets. */ export function useProjectAssets(projectId) {
    return useQuery({
        queryKey: assetsKey(projectId ?? ''),
        queryFn: ()=>artifactStudioClient.listProjectAssets({
                projectId: projectId ?? ''
            }),
        enabled: !!projectId
    });
}
/** Generate a first-draft artifact; on success the asset list refreshes. */ export function useGenerateFirstDraft(projectId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (vars)=>artifactStudioClient.generateFirstDraft({
                projectId,
                artifactType: vars.artifactType,
                title: vars.title ?? ''
            }),
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: assetsKey(projectId)
            })
    });
}
/** Advance an asset's approval state (in_review / approved / rejected). */ export function useTransitionApproval(projectId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (vars)=>artifactStudioClient.transitionApproval({
                id: vars.id,
                toState: vars.toState,
                reason: vars.reason ?? ''
            }),
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: assetsKey(projectId)
            })
    });
}
/** Validate + (when valid) audit-export a submission package of assets. */ export function useAssemblePackage() {
    return useMutation({
        mutationFn: (vars)=>artifactStudioClient.assembleSubmissionPackage({
                projectId: vars.projectId,
                assetIds: vars.assetIds,
                requiredArtifactTypes: vars.requiredArtifactTypes ?? []
            })
    });
}
